<!--
  DAVMAN AI INTEGRATION
  Este archivo es parte de la integración personalizada de IA para Davman Persa.
  NO es parte del código original de Chatwoot.

  Pestaña de configuración de personalidad del asistente IA
-->
<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { getPersonality, updatePersonality } from 'dashboard/api/davmanAi';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const loading = ref(true);
const saving = ref(false);
const personality = ref({
  assistant_name: '',
  system_prompt: '',
  tone_guidelines: [],
  brand_values: [],
  important_info: [],
});

// Temporary fields for adding new items
const newGuideline = ref('');
const newBrandValue = ref('');
const newImportantInfo = ref('');

const loadPersonality = async () => {
  loading.value = true;
  try {
    const data = await getPersonality();
    personality.value = {
      assistant_name: data.assistant_name || '',
      system_prompt: data.system_prompt || '',
      tone_guidelines: data.tone_guidelines || [],
      brand_values: data.brand_values || [],
      important_info: data.important_info || [],
    };
  } catch (error) {
    useAlert('Error al cargar la configuración de personalidad');
    console.error('[Davman AI] Error loading personality:', error);
  } finally {
    loading.value = false;
  }
};

const savePersonality = async () => {
  saving.value = true;
  try {
    await updatePersonality(personality.value);
    useAlert('Personalidad actualizada correctamente');
  } catch (error) {
    useAlert('Error al guardar la configuración');
    console.error('[Davman AI] Error saving personality:', error);
  } finally {
    saving.value = false;
  }
};

const addGuideline = () => {
  if (newGuideline.value.trim()) {
    personality.value.tone_guidelines.push(newGuideline.value.trim());
    newGuideline.value = '';
  }
};

const removeGuideline = (index) => {
  personality.value.tone_guidelines.splice(index, 1);
};

const addBrandValue = () => {
  if (newBrandValue.value.trim()) {
    personality.value.brand_values.push(newBrandValue.value.trim());
    newBrandValue.value = '';
  }
};

const removeBrandValue = (index) => {
  personality.value.brand_values.splice(index, 1);
};

const addImportantInfo = () => {
  if (newImportantInfo.value.trim()) {
    personality.value.important_info.push(newImportantInfo.value.trim());
    newImportantInfo.value = '';
  }
};

const removeImportantInfo = (index) => {
  personality.value.important_info.splice(index, 1);
};

onMounted(() => {
  loadPersonality();
});
</script>

<template>
  <div v-if="loading" class="flex items-center justify-center py-12">
    <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
  </div>

  <div v-else class="flex flex-col gap-8">
    <!-- Nombre del asistente -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Nombre del Asistente
      </label>
      <Input
        v-model="personality.assistant_name"
        placeholder="Ej: Asistente Davman Persa"
        class="max-w-md"
      />
      <p class="text-xs text-n-slate-11">
        El nombre que usará el asistente para presentarse.
      </p>
    </div>

    <!-- System Prompt -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Prompt del Sistema
      </label>
      <textarea
        v-model="personality.system_prompt"
        placeholder="Describe la personalidad y comportamiento del asistente..."
        rows="6"
        class="w-full px-3 py-2 font-mono text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 resize-y focus:border-n-strong focus:outline-none"
      />
      <p class="text-xs text-n-slate-11">
        Instrucciones base que definen cómo debe comportarse el asistente.
        Este texto se envía al modelo de IA como contexto inicial.
      </p>
    </div>

    <!-- Guías de tono -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Guías de Tono
      </label>
      <div class="flex flex-wrap gap-2 mb-2">
        <span
          v-for="(guideline, index) in personality.tone_guidelines"
          :key="index"
          class="flex items-center gap-2 px-3 py-1 text-sm rounded-full bg-n-solid-3"
        >
          {{ guideline }}
          <button
            class="i-lucide-x size-4 text-n-slate-11 hover:text-red-500"
            @click="removeGuideline(index)"
          />
        </span>
      </div>
      <div class="flex gap-2 max-w-md">
        <Input
          v-model="newGuideline"
          placeholder="Agregar guía de tono..."
          @keyup.enter="addGuideline"
        />
        <Button
          icon="i-lucide-plus"
          color="slate"
          faded
          @click="addGuideline"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        Directrices sobre el tono de comunicación (ej: "Siempre usar usted").
      </p>
    </div>

    <!-- Valores de marca -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Valores de Marca
      </label>
      <div class="flex flex-wrap gap-2 mb-2">
        <span
          v-for="(value, index) in personality.brand_values"
          :key="index"
          class="flex items-center gap-2 px-3 py-1 text-sm rounded-full"
          style="background-color: rgba(135, 120, 91, 0.15); color: #87785B;"
        >
          {{ value }}
          <button
            class="i-lucide-x size-4 hover:text-red-500"
            @click="removeBrandValue(index)"
          />
        </span>
      </div>
      <div class="flex gap-2 max-w-md">
        <Input
          v-model="newBrandValue"
          placeholder="Agregar valor de marca..."
          @keyup.enter="addBrandValue"
        />
        <Button
          icon="i-lucide-plus"
          color="slate"
          faded
          @click="addBrandValue"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        Valores que el asistente debe transmitir en sus respuestas.
      </p>
    </div>

    <!-- Información importante -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Información Importante Actual
      </label>
      <div class="flex flex-col gap-2 mb-2">
        <div
          v-for="(info, index) in personality.important_info"
          :key="index"
          class="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20"
        >
          <span class="i-lucide-info size-4 text-yellow-600" />
          <span class="flex-1 text-sm">{{ info }}</span>
          <button
            class="i-lucide-x size-4 text-n-slate-11 hover:text-red-500"
            @click="removeImportantInfo(index)"
          />
        </div>
      </div>
      <div class="flex gap-2 max-w-md">
        <Input
          v-model="newImportantInfo"
          placeholder="Agregar información importante..."
          @keyup.enter="addImportantInfo"
        />
        <Button
          icon="i-lucide-plus"
          color="slate"
          faded
          @click="addImportantInfo"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        Información que el asistente debe mencionar cuando sea relevante
        (ej: nuevas colecciones, promociones temporales).
      </p>
    </div>

    <!-- Botón guardar -->
    <div class="flex justify-end pt-4 border-t border-n-weak">
      <Button
        label="Guardar Cambios"
        icon="i-lucide-save"
        :is-loading="saving"
        @click="savePersonality"
      />
    </div>
  </div>
</template>
